/**
 * Submission Triage — Saved Views storage shape (OR-1384 / design OR-1507).
 *
 * A saved view is a named snapshot of the filters and column layout the
 * underwriter had applied, so they can re-apply it instead of rebuilding
 * the same filter set every morning.
 *
 * **Where it lives.** Backend OR-1385 gives us one opaque JSON blob per
 * (user, view_key) — deliberately opaque so frontend layout changes cost
 * no backend deploy. It does *not* model a collection of named views, so
 * the whole list rides inside that single blob. That fits: the blob is
 * capped at 32 KB, roughly two orders of magnitude more than the ~50
 * views the design's reference (HubSpot) allows. Trade-off to know
 * about: writes are last-write-wins per user, so the same user saving a
 * view in two tabs at once can lose one. Acceptable for preferences; if
 * views ever become shareable across the team (OR-1396) this needs real
 * rows instead.
 *
 * **Why one blob per tab.** Views are stored under the tab they were
 * created in (`submission_triage.submission` / `.facility`) because the
 * two tabs have different column sets — a Submissions view's column ids
 * are meaningless in the Facilities table.
 *
 * **Compatibility.** The blob stores column ids, and `triage-columns.ts`
 * changes with every deploy, so ids are always intersected with the
 * current column set on read: a saved view degrades, it never blanks the
 * table. Anything unreadable is dropped rather than half-applied.
 */
import type { ViewPreferenceConfig } from "../../api";
import { EMPTY_FILTERS, RENEWAL_PRESETS, type FilterOption, type RenewalPreset, type TriageFilters } from "./filters-drawer";
import { type TriageView, allColumnIds, defaultVisibleIds } from "./triage-columns";

/** Bump on any incompatible change to the stored shape. */
export const CONFIG_VERSION = 1;

/** Max length of a saved view's title, per the design's character counter. */
export const MAX_VIEW_NAME_LENGTH = 40;

/** How many views one user may keep per tab. Matches the design's
 *  HubSpot reference ("Add view (3/50)"). */
export const MAX_SAVED_VIEWS = 50;

/**
 * The layout the user last left the tab in — what OR-1384 actually asks
 * for: "the system should remember the user's last customized filter and
 * column settings, ensuring that their preferred layout persists even
 * after refreshing the page". Saved automatically on every commit, with
 * no UI of its own.
 *
 * `appliedViewIds` rides along because a view the user had applied *is*
 * part of the layout they left behind; without it the table would come
 * back filtered but the toolbar would have forgotten which view did it.
 *
 * Deliberately excludes the search box and the sort directive: a search
 * is a one-off lookup, not a preference, and the ticket scopes this to
 * "filter and column settings".
 */
export interface LastLayout {
    visibleColumnIds: string[];
    columnOrder: string[];
    filters: TriageFilters;
    appliedViewIds: string[];
}

export interface SavedView {
    id: string;
    name: string;
    visibleColumnIds: string[];
    columnOrder: string[];
    filters: TriageFilters;
    /** ISO timestamp — orders the dropdown oldest-first. */
    createdAt: string;
}

/** The layout a "Save current view" captures. */
export interface ViewSnapshot {
    visibleColumnIds: string[];
    columnOrder: string[];
    filters: TriageFilters;
}

export function newSavedView(name: string, snapshot: ViewSnapshot): SavedView {
    return {
        id: crypto.randomUUID(),
        name,
        ...snapshot,
        createdAt: new Date().toISOString(),
    };
}

// ───────────────────────────── serialize ─────────────────────────────

function sameIds(a: string[], b: string[]): boolean {
    return a.length === b.length && a.every((id, i) => id === b[i]);
}

/**
 * Column arrays are omitted when they match the tab's defaults, which is
 * the common case — most views change filters, not the layout, and the
 * natural order alone is ~450 bytes per view. Without this a user's 50
 * views would blow past the backend's 32 KB cap. The parser already
 * treats a missing array as "use the defaults", so the omission is
 * lossless.
 */
export function serializeBlob(
    views: SavedView[],
    layout: LastLayout | null,
    tab?: TriageView,
): ViewPreferenceConfig {
    const blob = serializeSavedViews(views, tab);
    if (layout) {
        blob.last_layout = {
            visible_column_ids: layout.visibleColumnIds,
            column_order: layout.columnOrder,
            filters: layout.filters,
            applied_view_ids: layout.appliedViewIds,
        };
    }
    return blob;
}

export function serializeSavedViews(views: SavedView[], tab?: TriageView): ViewPreferenceConfig {
    return {
        v: CONFIG_VERSION,
        saved_views: views.map((view) => {
            const entry: Record<string, unknown> = {
                id: view.id,
                name: view.name,
                filters: view.filters,
                created_at: view.createdAt,
            };
            const defaults = tab ? defaultVisibleIds(tab) : null;
            const natural = tab ? allColumnIds(tab) : null;
            if (!defaults || !sameIds(view.visibleColumnIds, defaults)) {
                entry.visible_column_ids = view.visibleColumnIds;
            }
            if (!natural || !sameIds(view.columnOrder, natural)) {
                entry.column_order = view.columnOrder;
            }
            return entry;
        }),
    };
}

/** Serialized size of a config, in the bytes the backend counts. */
export function configByteSize(config: ViewPreferenceConfig): number {
    return new TextEncoder().encode(JSON.stringify(config)).length;
}

/** The backend rejects a config over this; mirrored from
 *  `MAX_CONFIG_BYTES` in `src/schemas/v2/user_view_preference.py`. */
export const MAX_CONFIG_BYTES = 32 * 1024;

/** Whether a collection still fits the backend's cap. Checked before a
 *  write so an oversized blob surfaces as a message rather than a 422
 *  that silently loses the user's view. */
export function fitsConfigCap(views: SavedView[], tab: TriageView, layout: LastLayout | null = null): boolean {
    return configByteSize(serializeBlob(views, layout, tab)) <= MAX_CONFIG_BYTES;
}

// ──────────────────────────── deserialize ────────────────────────────

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asStringArray(value: unknown): string[] | null {
    if (!Array.isArray(value)) return null;
    return value.every((v) => typeof v === "string") ? (value as string[]) : null;
}

function asString(value: unknown): string {
    return typeof value === "string" ? value : "";
}

/** A `{ id, label }` option, or null for anything else (including the
 *  `null` we serialize for "no selection"). */
function asFilterOption(value: unknown): FilterOption | null {
    if (!isRecord(value)) return null;
    const { id, label } = value;
    if (typeof id !== "string" || typeof label !== "string") return null;
    return { id, label };
}

/**
 * Rebuild a column order: keep the saved sequence for columns that still
 * exist, then append any column the blob never knew about (added by a
 * later deploy). Never returns an id that isn't a real column.
 */
function reconcileOrder(view: TriageView, saved: string[] | null): string[] {
    const natural = allColumnIds(view);
    if (!saved) return natural;
    const known = new Set(natural);
    const kept = saved.filter((id) => known.has(id));
    const seen = new Set(kept);
    return [...kept, ...natural.filter((id) => !seen.has(id))];
}

/**
 * Visible ids, dropping columns that no longer exist. Falls back to the
 * tab's defaults when the saved set is missing or ends up empty — a table
 * with zero columns is broken, not a preference.
 */
function reconcileVisible(view: TriageView, saved: string[] | null): string[] {
    if (!saved) return defaultVisibleIds(view);
    const known = new Set(allColumnIds(view));
    const kept = saved.filter((id) => known.has(id));
    return kept.length > 0 ? kept : defaultVisibleIds(view);
}

/**
 * Field-by-field rebuild of a filter set. Anything malformed reverts to
 * that field's empty value rather than reaching the backend as a query
 * param — `filtersToParams` forwards these verbatim.
 */
/** A stored renewal preset, or "" when it isn't one we still offer. */
function asRenewalPreset(value: unknown): RenewalPreset | "" {
    const preset = asString(value);
    return (RENEWAL_PRESETS as readonly string[]).includes(preset) ? (preset as RenewalPreset) : "";
}

function reconcileFilters(saved: unknown): TriageFilters {
    if (!isRecord(saved)) return EMPTY_FILTERS;
    return {
        types: asStringArray(saved.types) ?? [],
        statuses: asStringArray(saved.statuses) ?? [],
        brokerage: asFilterOption(saved.brokerage),
        renewalPreset: asRenewalPreset(saved.renewalPreset),
        renewalStart: asString(saved.renewalStart),
        renewalEnd: asString(saved.renewalEnd),
        uw: asFilterOption(saved.uw),
        analyst: asFilterOption(saved.analyst),
        facilityCountMin: asString(saved.facilityCountMin),
        facilityCountMax: asString(saved.facilityCountMax),
        expiringDeductibleMin: asString(saved.expiringDeductibleMin),
        expiringDeductibleMax: asString(saved.expiringDeductibleMax),
    };
}

/** One stored view → a usable `SavedView`, or null when unreadable. A
 *  view without an id or a name can't be listed, applied or deleted. */
function parseSavedView(view: TriageView, raw: unknown): SavedView | null {
    if (!isRecord(raw)) return null;
    const { id, name } = raw;
    if (typeof id !== "string" || !id) return null;
    if (typeof name !== "string" || !name.trim()) return null;
    return {
        id,
        name: name.slice(0, MAX_VIEW_NAME_LENGTH),
        visibleColumnIds: reconcileVisible(view, asStringArray(raw.visible_column_ids)),
        columnOrder: reconcileOrder(view, asStringArray(raw.column_order)),
        filters: reconcileFilters(raw.filters),
        createdAt: asString(raw.created_at),
    };
}

/**
 * A saved blob → the user's views for one tab. Unknown versions, the
 * `{}` an unsaved slot returns, and unreadable entries all yield no
 * views rather than a broken list.
 */
export function parseSavedViews(view: TriageView, config: unknown): SavedView[] {
    if (!isRecord(config) || config.v !== CONFIG_VERSION) return [];
    const raw = config.saved_views;
    if (!Array.isArray(raw)) return [];
    const seen = new Set<string>();
    const views: SavedView[] = [];
    for (const entry of raw) {
        const parsed = parseSavedView(view, entry);
        // A duplicated id would make delete ambiguous — keep the first.
        if (!parsed || seen.has(parsed.id)) continue;
        seen.add(parsed.id);
        views.push(parsed);
    }
    return views.slice(0, MAX_SAVED_VIEWS);
}

/**
 * The stored last layout for `tab`, or null when nothing was saved or the
 * blob is unreadable. Column ids go through the same reconciliation as a
 * saved view's, so a stale id degrades the layout instead of blanking the
 * table.
 */
export function parseLastLayout(view: TriageView, config: unknown): LastLayout | null {
    if (!isRecord(config) || config.v !== CONFIG_VERSION) return null;
    const raw = config.last_layout;
    if (!isRecord(raw)) return null;
    return {
        visibleColumnIds: reconcileVisible(view, asStringArray(raw.visible_column_ids)),
        columnOrder: reconcileOrder(view, asStringArray(raw.column_order)),
        filters: reconcileFilters(raw.filters),
        appliedViewIds: asStringArray(raw.applied_view_ids) ?? [],
    };
}

// ───────────────────────── combining views ─────────────────────────

/**
 * Fold several filter sets into the one query the table runs.
 *
 * The design lets the user apply more than one saved view at a time and
 * combine those with ad-hoc filters, but doesn't say how conflicting
 * clauses resolve. Rules here, pending design confirmation:
 *  - `types` (a checkbox group) is a **union** — combining a Renewals
 *    view with a New Business view shows both.
 *  - every single-valued clause is **last non-empty wins**, callers
 *    passing ad-hoc filters last so a filter the user just set beats one
 *    that came in through a view.
 */
export function mergeFilters(sets: TriageFilters[]): TriageFilters {
    const merged: TriageFilters = { ...EMPTY_FILTERS };
    const types = new Set<string>();
    const statuses = new Set<string>();
    for (const set of sets) {
        for (const type of set.types) types.add(type);
        for (const status of set.statuses) statuses.add(status);
        if (set.brokerage) merged.brokerage = set.brokerage;
        if (set.uw) merged.uw = set.uw;
        if (set.analyst) merged.analyst = set.analyst;
        // Preset and range share `renewal_date`: the later one wins whole,
        // never a preset mixed with half a range.
        if (set.renewalPreset) {
            merged.renewalPreset = set.renewalPreset;
            merged.renewalStart = "";
            merged.renewalEnd = "";
        } else if (set.renewalStart || set.renewalEnd) {
            merged.renewalPreset = "";
            if (set.renewalStart) merged.renewalStart = set.renewalStart;
            if (set.renewalEnd) merged.renewalEnd = set.renewalEnd;
        }
        if (set.facilityCountMin) merged.facilityCountMin = set.facilityCountMin;
        if (set.facilityCountMax) merged.facilityCountMax = set.facilityCountMax;
        if (set.expiringDeductibleMin) merged.expiringDeductibleMin = set.expiringDeductibleMin;
        if (set.expiringDeductibleMax) merged.expiringDeductibleMax = set.expiringDeductibleMax;
    }
    merged.types = [...types];
    merged.statuses = [...statuses];
    return merged;
}
